'use client';

import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../utils/cn";

/**
 * Progress — determinate or indeterminate progress bar.
 *
 * Modes:
 *   determinate    — pass `value` between 0-100 for a fill.
 *   indeterminate  — omit `value` (or pass `null`); shows looping animation.
 *
 * Sizes: 4 | 6 | 8 (bar height in px). Default: 6.
 * Tones: default (violet) | success | warn | error.
 */

const rootVariants = cva(
  "relative w-full overflow-hidden rounded-chip bg-chrome-panel",
  {
    variants: {
      size: {
        4: "h-1",
        6: "h-1.5",
        8: "h-2",
      },
    },
    defaultVariants: { size: 6 },
  },
);

const indicatorVariants = cva(
  "h-full transition-transform duration-standard ease-standard",
  {
    variants: {
      tone: {
        default: "bg-accent-violet",
        success: "bg-status-success",
        warn: "bg-status-warn",
        error: "bg-status-error",
      },
    },
    defaultVariants: { tone: "default" },
  },
);

export interface ProgressProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
      "value"
    >,
    VariantProps<typeof rootVariants>,
    VariantProps<typeof indicatorVariants> {
  /**
   * 0-100 for determinate. Omit / null for indeterminate.
   * Values outside [0, 100] are clamped.
   */
  value?: number | null;
  /** Optional aria label; falls back to "Loading" when indeterminate. */
  label?: string;
}

export const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(function Progress(
  { className, value, size, tone, label, ...props },
  ref,
) {
  const indeterminate = value === null || value === undefined;
  const clamped = indeterminate ? 0 : Math.max(0, Math.min(100, value!));

  return (
    <ProgressPrimitive.Root
      ref={ref}
      data-slot="progress"
      data-indeterminate={indeterminate || undefined}
      value={indeterminate ? null : clamped}
      aria-label={label ?? (indeterminate ? "Loading" : undefined)}
      className={cn(rootVariants({ size, className }))}
      {...props}
    >
      {indeterminate ? (
        <span
          data-slot="progress-indicator"
          className={cn(
            indicatorVariants({ tone }),
            "block absolute inset-y-0 w-1/3 animate-[progress-indeterminate_1.2s_ease-in-out_infinite]",
          )}
          style={{ willChange: "transform" }}
        />
      ) : (
        <ProgressPrimitive.Indicator
          data-slot="progress-indicator"
          className={cn(indicatorVariants({ tone }))}
          style={{ transform: `translateX(-${100 - clamped}%)` }}
        />
      )}
    </ProgressPrimitive.Root>
  );
});
Progress.displayName = "Progress";

export { rootVariants as progressRootVariants, indicatorVariants as progressIndicatorVariants };
